import {
  DEAL_STATUSES,
  ShortReservation,
  ShortGuest,
  Offer,
  OFFER_CONFIRMATION_TYPES,
  type RadicalStorageDealSelection,
} from '@chekin/core';
import {
  useBookExperienceStore,
  useItemQuantitiesByOfferId,
  selectEmail,
  selectEmailReservationId,
} from 'store/bookExperience';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useGuestGroup} from './useGuestGroup';
import {useFetchOffer} from './useFetchOffer';
import {useCreateBulkDealMutation} from './useCreateBulkDealMutation';
import {usePath} from './common';
import {useReservation} from 'context/reservation';
import {PATHS} from 'routes/constants';
import {BACK_TO_OFFER_LIST} from 'components/upselling/usePathToOfferView';
import {useAnalytics, POSTHOG_EVENTS} from 'analytics';
import {buildRequestedForOffer, buildRequestedItemsForOffer} from 'utils/upselling';
import {isFirstLoad} from 'utils/query';
import {usePaymentsEntryPath} from './payments/usePaymentsEntryPath';

type LuggageSelection = RadicalStorageDealSelection;

type BuildPayloadProps = {
  reservation: ShortReservation;
  offer: Offer;
  email?: string;
  coreGuest?: ShortGuest;
  itemQuantities: Record<string, number>;
  requestedFor?: string | null;
  luggage?: LuggageSelection | null;
};

export const buildDealPayload = ({
  reservation,
  email,
  offer,
  coreGuest,
  itemQuantities,
  requestedFor,
  luggage,
}: BuildPayloadProps) => {
  const {requestedItems, numberOfPeople} = buildRequestedItemsForOffer(
    offer,
    itemQuantities,
  );

  return {
    deals: [
      {
        core_housing_id: reservation?.housing_id,
        core_reservation_id: reservation?.id,
        core_guest_name: coreGuest?.full_name,
        core_housing_name: reservation.housing_name,
        core_room_numbers: reservation.rooms.map(room => room.number),
        guest_email: email,
        core_guest_id: coreGuest?.id,
        offer_id: offer.id,
        offerName: offer.title,
        quantity: 1,
        payments_version: 'V2' as const,
        number_of_people: numberOfPeople,
        requested_items: requestedItems,
        check_in_date: reservation.check_in_date?.slice(0, 10) ?? null,
        check_out_date: reservation.check_out_date?.slice(0, 10) ?? null,
        requested_for:
          requestedFor !== undefined
            ? requestedFor
            : buildRequestedForOffer(
                offer,
                itemQuantities,
                reservation.check_in_date?.slice(0, 10),
                reservation.check_out_date?.slice(0, 10),
              ),
        status:
          offer.confirmation_type === OFFER_CONFIRMATION_TYPES.auto
            ? DEAL_STATUSES.approved
            : DEAL_STATUSES.pre_requested,
        ...(luggage ? {luggage} : {}),
      },
    ],
  };
};

type Props = {
  offerId?: string;
  onSuccess?: () => void;
  onError?: (errors: Error[]) => void;
  deliveryDateTime?: string | null;
  luggage?: LuggageSelection | null;
};
export const useBookDeal = ({
  offerId,
  onSuccess,
  onError,
  deliveryDateTime,
  luggage,
}: Props) => {
  const [searchParams] = useSearchParams();
  const storedEmail = useBookExperienceStore(selectEmail);
  const storedEmailReservationId = useBookExperienceStore(selectEmailReservationId);
  const {data: reservation, isLoading: isLoadingReservation} = useReservation();
  const offerQuery = useFetchOffer({id: offerId || ''});
  const offer = offerQuery.data;
  const isLoadingOffer = isFirstLoad(offerQuery);
  const {guestGroup} = useGuestGroup();
  const itemQuantities = useItemQuantitiesByOfferId(offerId);
  const setBookExperienceEmail = useBookExperienceStore(state => state.setEmail);
  const resetBookExperienceItemQuantities = useBookExperienceStore(
    state => state.resetItemQuantities,
  );
  const {getFullPath} = usePath();
  const navigate = useNavigate();
  const paymentsEntryPath = usePaymentsEntryPath();
  const {track, trackOffer} = useAnalytics();

  const isBackToOfferList = searchParams.has(BACK_TO_OFFER_LIST);

  const {mutate, isPending: isCreating} = useCreateBulkDealMutation({
    onSuccess() {
      const {requestedItems} = buildRequestedItemsForOffer(offer, itemQuantities);
      const trackedQuantity = requestedItems.reduce(
        (sum, item) => sum + item.quantity,
        0,
      );

      if (offer) {
        trackOffer(POSTHOG_EVENTS.OFFER_MANUALLY_ADDED, offer, {
          quantity: trackedQuantity,
        });
      } else {
        track(POSTHOG_EVENTS.OFFER_MANUALLY_ADDED, {quantity: trackedQuantity});
      }
      if (luggage) {
        track(POSTHOG_EVENTS.LUGGAGE_BOOKING_COMPLETED, {
          storage_id: luggage.storage_id,
          bags: trackedQuantity,
        });
      }
      onSuccess?.();
      if (offerId) {
        resetBookExperienceItemQuantities(offerId);
      }
    },
    onError(errors) {
      if (luggage) {
        track(POSTHOG_EVENTS.LUGGAGE_BOOKING_FAILED, {
          storage_id: luggage.storage_id,
          reason: errors?.[0]?.message,
        });
      }
      onError?.(errors);
    },
  });

  const createDeal = (email?: string) => {
    if (email) {
      setBookExperienceEmail(email, reservation?.id ?? null);
    }
    const coreGuest = guestGroup?.members.find(({id}) => id === guestGroup?.leader_id);

    const hasItemQuantities = Object.values(itemQuantities).some(q => q > 0);

    if (!isCreating && reservation && offer && hasItemQuantities) {
      if (luggage) {
        track(POSTHOG_EVENTS.LUGGAGE_BOOKING_INITIATED, {
          storage_id: luggage.storage_id,
        });
      }
      mutate(
        buildDealPayload({
          reservation,
          offer,
          coreGuest,
          email,
          itemQuantities,
          requestedFor: deliveryDateTime,
          luggage,
        }),
      );
    }
  };

  const bookingEmail =
    guestGroup?.members.find(({id}) => id === guestGroup?.leader_id)?.email ||
    reservation?.default_invite_email ||
    '';

  const navigateAfterSuccess = () => {
    if (isBackToOfferList) {
      navigate(getFullPath(PATHS.recommendations.main));
    } else {
      navigate(getFullPath(PATHS.home));
    }
  };

  const navigateToCart = () => {
    navigate(getFullPath(paymentsEntryPath));
  };

  const navigateToRecommendations = () => {
    navigate(getFullPath(PATHS.recommendations.main));
  };

  const storedReplyEmail =
    reservation?.id && storedEmailReservationId === reservation.id ? storedEmail : '';

  const replyEmail = storedReplyEmail || bookingEmail;

  const setReplyEmail = (email: string) =>
    setBookExperienceEmail(email, reservation?.id ?? null);

  return {
    createDeal,
    setReplyEmail,
    replyEmail,
    navigateAfterSuccess,
    navigateToCart,
    navigateToRecommendations,
    offer,
    itemQuantities,
    housingId: reservation?.housing_id ?? null,
    defaultBags: guestGroup?.known_number_of_guests || 1,
    checkInDate: reservation?.check_in_date?.slice(0, 10) ?? null,
    checkOutDate: reservation?.check_out_date?.slice(0, 10) ?? null,
    isLoading: isLoadingReservation || isLoadingOffer,
    isCreating,
  };
};
